import { Dialog, DialogContent, Typography, DialogActions, Button, Box, TextField, Checkbox } from '@mui/material';
import AddMachinesConcerneeListViewData from './addMachinesConcerneeListViewData';
import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { useState, useEffect, useRef, useCallback } from 'react';
import CustomHeaderCell from '../../utils/customHeaderCell';
import { isObjectEmpty } from '../../utils/isObjectEmpty';
import useDebounce from '../../hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { FixedSizeList } from "react-window";
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import Iconify from '../Iconify';
import axios from 'axios';
import '../../css/addMachinesConcernee.css';

const machine_status = [
    { id: 'Valide', name: 'Valide' },
    { id: 'AvecReparation', name: 'Avec Reparation' },
    { id: 'NonConforme', name: 'Non Conforme' },
];

export const AddMachinesConcernee = ({ chooseSingleMachine, openAddMachines, setOpenAddMachines, addMachines, selectedMachines, setSelectedMachines, allMachinesSelected, setAllMachinesSelected, addMachinesFilter, setAddMachinesFilter, machine_ids }) => {

    const { t } = useTranslation();
    const scrollRef = useRef(null);
    const tableHeadRef = useRef(null);
    const authenticatedUser = useSelector((state) => state.AuthenticatedUser);

    const [search, setSearch] = useState(addMachinesFilter.debouncedSearch || '');
    const [machines, setMachines] = useState([]);
    const [regions, setRegions] = useState([]);
    const [sites, setSites] = useState([]);
    const [poles, setPoles] = useState([]);
    const [metiers, setMetiers] = useState([]);
    const [brands, setBrands] = useState([]);
    const [status, setStatus] = useState([]);
    const [totalMachines, setTotalMachines] = useState(0);
    const [offset, setOffset] = useState(0);
    const [machinesFetched, setMachinesFetched] = useState(0);
    const [filteredMachinesCount, setFilteredMachinesCount] = useState(0);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    const limit = 30;
    const debouncedSearch = useDebounce(search, 1000);

    const [machineConcerneeColumnWidth, setMachineConcerneeColumnWidth] = useState(
        JSON.parse(localStorage.getItem('machineConcerneeColumnWidth')) ||
        {
            name: 250,
            region: 250,
            site: 250,
            pole: 250,
            metier: 250,
            marque: 250,
            status: 250,
            type: 250,
        }
    );

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredMachinesCount, setMachinesFetched, 70, 440);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setMachinesFetched, filteredMachinesCount, 70, 440);

    const filtersEmpty = isObjectEmpty(addMachinesFilter);

    // Update filters when debounced search changes
    useEffect(() => {
        setAddMachinesFilter((prev) => ({ ...prev, debouncedSearch: debouncedSearch }));
    }, [debouncedSearch, setAddMachinesFilter]);

    // Scroll to top when filters change
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo(0);
        }
    }, [addMachinesFilter]);

    const fetchMachines = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('machine', { params: { offset: adjustedOffset, limit, filters: addMachinesFilter, machine_ids: chooseSingleMachine ? [] : machine_ids } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedMachines = [];
                // Update the fetched records in their correct positions
                response?.data?.machines.forEach((file, index) => { updatedMachines[adjustedOffset + index] = file });
                // Update the state with the new files array
                setMachines(updatedMachines);
                setFilteredMachinesCount(response?.data?.filteredMachinesCount);
            }
        } catch (error) {
            console.error('Error Fetching machines in addMachinesConcernee:', error);
            CustomErrorLogger(error, 'Error Fetching machines in addMachinesConcernee');
        }
    }, [offset, addMachinesFilter, machine_ids, chooseSingleMachine]);


    const fetchMachinesFiltrationArrays = useCallback(async () => {
        try {
            const response = await axios.get('machine/getMachineFiltrationArrays', { params: { filters: addMachinesFilter } });
            if (response.status === 200 || response.status === 201) {
                setRegions(response?.data?.regionArray);
                setSites(response?.data?.siteArray);
                setPoles(response?.data?.poleArray);
                setMetiers([...response?.data?.metierArray]);
                setBrands([...response?.data?.brandArray]);
                setStatus(response?.data?.statusArray);
                setTotalMachines(response?.data?.allMachinesCount);
            }
        } catch (error) {
            console.error('Error Fetching machines filtration arrays:', error);
            CustomErrorLogger(error, 'fetch machines filtration arrays in machines page');
        }
    }, [addMachinesFilter]);

    useEffect(() => {
        fetchMachines()
    }, [fetchMachines]);

    useEffect(() => {
        fetchMachinesFiltrationArrays()
    }, [fetchMachinesFiltrationArrays]);

    const handleMachineClick = useCallback((id) => {
        if (chooseSingleMachine) {
            setSelectedMachines(id);
            return;
        }
        setSelectedMachines((prev) => {
            if (prev.includes(id)) {
                return prev.filter((machineId) => machineId !== id);
            } else {
                return [...prev, id];
            }
        });
    }, [setSelectedMachines, chooseSingleMachine]);

    const handleClose = () => {
        setOpenAddMachines(false);
        setSelectedMachines(chooseSingleMachine ? '' : []);
        if (!chooseSingleMachine) {
            setAllMachinesSelected(false)
        }
    }

    const handleChangeRegion = (region) => {
        setAddMachinesFilter({ ...addMachinesFilter, selectedRegion: region, selectedSite: '', selectedPole: '', selectedMetier: '', selectedStatus: '', selectedType: '', selectedMark: '' });
    }

    const handleChangeSite = (site) => {
        setAddMachinesFilter({ ...addMachinesFilter, selectedSite: site, selectedPole: '', selectedMetier: '', selectedStatus: '', selectedType: '', selectedMark: '' });
    }

    const handleChangePole = (pole) => {
        setAddMachinesFilter({ ...addMachinesFilter, selectedPole: pole, selectedMetier: '', selectedStatus: '', selectedType: '', selectedMark: '' });
    }

    const handleChangeMetier = (metier) => {
        setAddMachinesFilter({ ...addMachinesFilter, selectedMetier: metier, selectedStatus: '', selectedType: '', selectedMark: '' });
    }

    const handleChangeStatus = (status) => {
        setAddMachinesFilter({ ...addMachinesFilter, selectedStatus: status });
    }

    const handleChangeMark = (mark) => {
        setAddMachinesFilter({ ...addMachinesFilter, selectedMark: mark });
    }

    const handleColumnWidthChange = (value, columnKey) => {
        setMachineConcerneeColumnWidth({ ...machineConcerneeColumnWidth, [columnKey]: value })
    }

    const handleResetFilters = useCallback(() => {
        setAddMachinesFilter({
            selectedRegion: '',
            selectedSite: '',
            selectedPole: '',
            selectedMetier: '',
            selectedStatus: '',
            selectedType: '',
            selectedMark: '',
            debouncedSearch: ''
        });
        setSearch('');
    }, [setAddMachinesFilter]);

    useEffect(() => {
        localStorage.setItem('machineConcerneeColumnWidth', JSON.stringify(machineConcerneeColumnWidth));
    }, [machineConcerneeColumnWidth]);

    const Row = ({ index, style, data }) => {
        const { machines, regions, handleMachineClick } = data;
        const machine = index === 0 ? machines[0] : machines[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <AddMachinesConcerneeListViewData
                    chooseSingleMachine={chooseSingleMachine}
                    machine={machine}
                    regions={regions}
                    columnWidth={machineConcerneeColumnWidth}
                    selectedMachines={selectedMachines}
                    handleMachineClick={handleMachineClick}
                    allMachinesSelected={allMachinesSelected}
                />
            </Box>
        );
    };

    return (
        <Dialog open={openAddMachines} fullWidth maxWidth="lg">
            <Box onClick={() => setOpenAddMachines(false)} className='addMachinesConcernee_icon_text'>
                <Typography className='addMachinesConcernee_title'>{chooseSingleMachine ? 'Selectionner une Machine' : 'Ajouter des Machines'}</Typography>
                <Iconify icon={"material-symbols:close"} className='addMachinesConcernee_close_icon' />
            </Box>
            <DialogContent className='addMachinesConcernee_dialog_content' >
                <Box className='addMachinesConcernee_filtration_box'>
                    <Box className='addMachinesConcernee_search_box'>
                        <TextField
                            size="small"
                            value={search}
                            placeholder='Rechercher'
                            onChange={(e) => setSearch(e.target.value)}
                            variant="outlined"
                            InputProps={{
                                startAdornment: <Iconify icon={"bx:bx-search"} className='addMachinesConcernee_search_icon' />,
                                sx: {
                                    backgroundColor: '#fff',
                                    height: '36px',
                                    fontFamily: 'Mulish',
                                    color: '#201E1E',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                },
                            }}
                        />
                        <Typography className='addMachinesConcernee_clearAll' onClick={handleResetFilters}>Re-initialiser</Typography>
                    </Box>
                    <Typography className='MachineFiltration_total_number'>
                        {machinesFetched} affichés
                        {!filtersEmpty && ` / ${filteredMachinesCount} filtrés`}
                        {` / ${totalMachines} au total`}
                    </Typography>
                </Box>
                {!chooseSingleMachine &&
                    <Box className='addMachinesConcernee_selectAll_box'>
                        <Checkbox
                            sx={{
                                '&.Mui-checked': {
                                    color: '#EA5E00',
                                },
                            }}
                            checked={allMachinesSelected}
                            onChange={() => setAllMachinesSelected(!allMachinesSelected)} />
                        <Typography className='addMachinesConcernee_selectAll_text'>Tout sélectionner</Typography>
                    </Box>
                }
                <Box className='table'>
                    <Box className='tableHead' ref={tableHeadRef}>
                        <CustomHeaderCell
                            className='addMachinesConcernee_table_header_cell'
                            label={t('machineName')}
                            columnKey='name'
                            isFilter={false}
                            isFixed={true}
                            columnWidth={machineConcerneeColumnWidth}
                            onColumnWidthChange={handleColumnWidthChange}
                        />
                        {regions?.length > 1 && (
                            <CustomHeaderCell
                                className='addMachinesConcernee_table_header_cell'
                                label={t('Regions')}
                                columnKey='region'
                                isFilter={regions?.length > 1}
                                value={addMachinesFilter.selectedRegion}
                                onChange={(e) => handleChangeRegion(e.target.value)}
                                options={regions}
                                columnWidth={machineConcerneeColumnWidth}
                                onColumnWidthChange={handleColumnWidthChange}
                            />
                        )}
                        <CustomHeaderCell
                            className='addMachinesConcernee_table_header_cell'
                            label={t('sites')}
                            columnKey='site'
                            isFilter={authenticatedUser?.user_site_ids?.length > 1}
                            value={addMachinesFilter.selectedSite}
                            onChange={(e) => handleChangeSite(e.target.value)}
                            options={sites}
                            columnWidth={machineConcerneeColumnWidth}
                            onColumnWidthChange={handleColumnWidthChange}
                        />
                        <CustomHeaderCell
                            className='addMachinesConcernee_table_header_cell'
                            label={t('poles')}
                            columnKey='pole'
                            isFilter={authenticatedUser?.user_pole_ids?.length > 1}
                            value={addMachinesFilter.selectedPole}
                            onChange={(e) => handleChangePole(e.target.value)}
                            options={poles}
                            columnWidth={machineConcerneeColumnWidth}
                            onColumnWidthChange={handleColumnWidthChange}
                        />
                        <CustomHeaderCell
                            className='addMachinesConcernee_table_header_cell'
                            label={t('Métier')}
                            columnKey='metier'
                            isFilter={authenticatedUser?.user_metier_ids?.length > 1}
                            value={addMachinesFilter.selectedMetier}
                            onChange={(e) => handleChangeMetier(e.target.value)}
                            options={metiers}
                            columnWidth={machineConcerneeColumnWidth}
                            onColumnWidthChange={handleColumnWidthChange}
                        />
                        <CustomHeaderCell
                            className='addMachinesConcernee_table_header_cell'
                            label={t('Statut de contrôle')}
                            columnKey='status'
                            isFilter={true}
                            value={addMachinesFilter?.selectedStatus}
                            onChange={(e) => handleChangeStatus(e.target.value)}
                            options={[...machine_status.filter((ms) => status.includes(ms.id)), { id: 'Indéfini', name: 'Indéfini' }]}
                            columnWidth={machineConcerneeColumnWidth}
                            onColumnWidthChange={handleColumnWidthChange}
                        />
                        <CustomHeaderCell
                            className='addMachinesConcernee_table_header_cell'
                            label={t('Marque')}
                            columnKey='marque'
                            isFilter={true}
                            value={addMachinesFilter?.selectedMark}
                            onChange={(e) => handleChangeMark(e.target.value)}
                            options={brands}
                            columnWidth={machineConcerneeColumnWidth}
                            onColumnWidthChange={handleColumnWidthChange}
                        />
                        <CustomHeaderCell width="50px" className='addMachinesConcernee_table_header_cell' />
                    </Box>
                    <Box className='tableBody'>
                        <FixedSizeList
                            ref={scrollRef}
                            height={windowHeight - 440}
                            width='100%'
                            itemCount={filteredMachinesCount}
                            itemSize={70}
                            itemData={{ machines, regions, handleMachineClick }}
                            onScroll={handleScrollList}
                        >
                            {Row}
                        </FixedSizeList>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions className='addMachinesConcernee_dialog_actions'>
                <Button className='addMachinesConcernee_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                <Button className='addMachinesConcernee_button_valider' variant="contained" onClick={addMachines}>{chooseSingleMachine ? 'Valider' : `Ajouter ${allMachinesSelected ? filteredMachinesCount : selectedMachines.length} machines`}</Button>
            </DialogActions>
        </Dialog>
    );
};